/* Atlas v3 — TickerSheet: the expandable bottom sheet (peek ⇄ full drag) that
   opens from ANY ticker tap. Replaces the old StockDetail right-drawer.
   Three tabs: Overview · Intel · Outlook.

   ES-module port of the locked mockup (window.TickerSheet). The mockup's
   synthetic genOHLC / buildPerf / buildCatalysts / THESIS placeholders are
   replaced with REAL data:
     • CandleChart  → real OHLCV bars from /api/chart-bars (candles + volume).
     • PerfGrid     → real per-timeframe % from the live snapshot change buckets.
     • Key Stats    → real Finnhub fundamentals (useFundamentals → /api/fundamentals).
     • Intel        → real stock fields (rating / thesis / cron_summary / risk).
     • Outlook      → real next-earnings from useEarnings() filtered to ticker.
   The CandleChart RENDERING (candles+volume plot, crosshair, line/candle
   toggle, price axis, last-price tag) is kept faithful to the mockup. */
import { useState, useRef, useEffect, useMemo } from 'react'
import { CandleChart, DEFAULT_INDICATORS } from './CandleChart'
import { ChartSettingsPanel, AdvancedChart } from './ChartSettings'
import { useUserPref } from '@/v2/hooks/useUserPref'
import { ANALYSIS_PAUSED } from '@/v2/lib/analysisPaused'
import { tok, REDUCE, SkeletonRow } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { stripNamePrefix } from '@/v2/lib/descriptionFormat'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useEarnings } from '@/v2/hooks/useSupabaseData'
import { useOvernightSplit } from '@/v2/hooks/useOvernightSplit'
import { useEarningsHistory, useEarningsQuarter } from '@/v2/hooks/useEarningsHistory'
import AskCalls from './AskCalls'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const SNAP = { peek: 56, full: 95 }
const TICKER_TF = ['1D', '5D', '1M', '6M', '1Y', '5Y', 'MAX']
// Chart types that are candle-family — the ▦ pill button stays active when
// the settings panel selects one of the richer candle renderings.
const CANDLE_KINDS = { candles: true, candle: true, hollow: true, heikin: true }
// Timeframes whose own series is too short for the longer indicators, mapped to
// the range fetched purely as lead-in. 1M is ~25 daily bars (RANGE_CONFIG in
// api/chart-bars.js) and MACD needs ~35, SMA 200 needs 200 — so on 1M those
// indicators could not exist at all. 1Y is ~260 daily bars of the same
// interval, which covers every indicator we offer. Intraday ranges (1D/5D)
// aren't padded: their indicators are meant to read off intraday bars.
const WARMUP_TF = { '1M': '1Y', '6M': '1Y' }
// Map a timeframe chip to the matching pre-computed % field on the live snapshot.
const CHANGE_FIELD = { '1D': 'change1d', '5D': 'change1w', '1M': 'change1m', '6M': 'change6m', '1Y': 'change1y' }
// The two timeframes with no live snapshot bucket — they read price_history
// through /api/overnight-split, so they're as-of last night's close, not live.
const IS_EOD_TF = { '5Y': true, MAX: true }

/* ── ported helpers (verbatim from live StockDetail) ── */
function relTime(isoStr) {
  if (!isoStr) return ''
  const diff = Date.now() - new Date(isoStr).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
// `stocks.thesis` is overloaded: seeded with category enums (e.g. "AI_CLOUD").
// Real prose has lowercase letters or spaces, so it passes through unchanged.
function isEnumThesis(s) {
  if (!s || typeof s !== 'string') return false
  return /^[A-Z][A-Z0-9_]+$/.test(s.trim())
}
function fmtMcap(m) {
  if (!m) return null
  if (m >= 1_000_000) return `$${(m / 1_000_000).toFixed(2)}T`
  if (m >= 1_000) return `$${(m / 1_000).toFixed(1)}B`
  return `$${m.toFixed(0)}M`
}

const Kk = (txt, color) => (
  <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: color || 'hsl(var(--studio-accent))' }}>{txt}</div>
)

/* ── live fundamentals (Finnhub, same endpoint the old drawer used) ── */
function useFundamentals(ticker) {
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!ticker) { setData(null); return }
    let cancelled = false
    setData(null)
    fetch(`/api/fundamentals?ticker=${encodeURIComponent(ticker)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled && d && !d.error) setData(d) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [ticker])
  return data
}

/* ── real OHLCV bars for the candle chart ── */
function useBars(ticker, range, interval) {
  const [bars, setBars] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    if (!ticker || !range) { setBars(null); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    const ctrl = new AbortController()
    // interval is intraday-only; the endpoint ignores it on daily ranges.
    const q = `/api/chart-bars?ticker=${encodeURIComponent(ticker)}&range=${range}${interval ? `&interval=${interval}` : ''}`
    fetch(q, { signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled) { setBars(Array.isArray(d?.bars) ? d.bars : null); setLoading(false) } })
      .catch((e) => { if (e.name !== 'AbortError' && !cancelled) { setBars(null); setLoading(false) } })
    return () => { cancelled = true; ctrl.abort() }
  }, [ticker, range, interval])
  return { bars, loading }
}

/* ── collapsible description row — borderless, hairline divider ── */
function Disclosure({ label, body }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid hsl(var(--studio-border) / 0.5)' }}>
      <button onClick={() => setOpen((v) => !v)} aria-expanded={open}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '12px 2px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-muted))' }}>
        <span style={{ flex: 1, textTransform: 'uppercase' }}>{label}</span>
        <span style={{ flexShrink: 0, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', display: 'inline-block', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 160ms var(--studio-ease)' }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: '0 2px 13px', fontSize: 12.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', whiteSpace: 'pre-wrap', fontFamily: 'var(--studio-font-sans)' }}>{body}</div>
      )}
    </div>
  )
}

/* ── key-stat cell — borderless, hairline-divided grid ── */
function StatPill({ label, value, color, index, total }) {
  const vc = color || '--studio-text'
  const col = index % 2, row = Math.floor(index / 2), lastRow = Math.floor((total - 1) / 2)
  return (
    <div style={{ padding: col === 0 ? '13px 14px 13px 0' : '13px 0 13px 15px',
      borderRight: col === 0 ? '1px solid hsl(var(--studio-border) / 0.5)' : 'none',
      borderBottom: row < lastRow ? '1px solid hsl(var(--studio-border) / 0.5)' : 'none' }}>
      <div style={{ ...Mono, fontSize: 16, color: `hsl(var(${vc}))`, fontVariantNumeric: 'tabular-nums', marginBottom: 5 }}>{value}</div>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>{label}</div>
    </div>
  )
}

/* ── watch star — borderless, amber OUTLINE, fills when favorited ── */
function WatchBtn({ watch, onClick }) {
  const [pressed, setPressed] = useState(false)
  return (
    <button onClick={onClick} aria-label={watch ? 'Remove from watchlist' : 'Add to watchlist'}
      onPointerDown={() => setPressed(true)} onPointerUp={() => setPressed(false)} onPointerCancel={() => setPressed(false)} onPointerLeave={() => setPressed(false)}
      style={{ width: 30, height: 30, flexShrink: 0, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0,
        color: 'hsl(var(--studio-accent))', fontSize: 17, lineHeight: 1, opacity: pressed ? 0.55 : 1, transition: 'opacity 140ms var(--studio-ease)' }}>
      {watch ? '★' : '☆'}
    </button>
  )
}

/* ── price-performance — hairline-separated timeframe row, amber underline = active ── */
// Compact % so a long-window cell can't overflow its column now that there are
// seven of them (~52px each at 390px). NVDA's MAX is +15,531% — "+15531.0%"
// blows the cell, "+15.5k" doesn't. Under 1,000 this renders exactly as before.
const fmtPct = (v) => {
  const a = Math.abs(v)
  return `${v < 0 ? '-' : '+'}${a >= 1000 ? `${(a / 1000).toFixed(1)}k` : a.toFixed(1)}`
}
// Header variant: the ▲/▼ already carries direction, so this is the magnitude
// only — 2dp, k-suffixed past 1,000 so a MAX return ("+1632.30%") doesn't
// overflow the header cell now that 5Y/MAX reach it.
const fmtAbsPct = (v) => {
  const a = Math.abs(v)
  return a >= 1000 ? `${(a / 1000).toFixed(1)}k` : a.toFixed(2)
}

function PerfGrid({ perf, tf, onPick }) {
  return (
    <div style={{ display: 'flex' }}>
      {TICKER_TF.map((k, i) => {
        const v = perf[k]
        const c = v == null ? '--studio-text-subtle' : v >= 0 ? '--studio-success' : '--studio-danger'
        const on = tf === k
        return (
          <button key={k} onClick={() => onPick && onPick(k)}
            style={{ flex: 1, textAlign: 'center', padding: '4px 2px 9px', cursor: onPick ? 'pointer' : 'default', background: 'transparent', border: 'none',
              borderLeft: i === 0 ? 'none' : '1px solid hsl(var(--studio-border) / 0.5)',
              borderBottom: on ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', transition: 'border-color 160ms var(--studio-ease)' }}>
            <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', color: on ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))', marginBottom: 5 }}>{k}</div>
            <div style={{ ...Mono, fontSize: 12, color: `hsl(var(${c}))`, fontVariantNumeric: 'tabular-nums' }}>{v == null ? '—' : `${fmtPct(v)}%`}</div>
          </button>
        )
      })}
    </div>
  )
}

/* ── news-sentiment — borderless row, hairline divider, inline disclosure ── */
function Sentiment({ s }) {
  const [open, setOpen] = useState(false)
  if (!s) return null
  const color = s.pct >= 60 ? '--studio-success' : s.pct <= 40 ? '--studio-danger' : '--studio-text-subtle'
  const label = s.pct >= 60 ? 'BULLISH' : s.pct <= 40 ? 'BEARISH' : 'MIXED'
  return (
    <div style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 2px', cursor: 'pointer', background: 'transparent', border: 'none' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: `hsl(var(${color}))` }} />
          <span style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-muted))' }}>NEWS SENTIMENT</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ ...Mono, fontSize: 12.5, color: `hsl(var(${color}))`, fontVariantNumeric: 'tabular-nums' }}>{s.pct}%</span>
          <span style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }}>▾</span>
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 2px 13px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ ...Mono, fontSize: 24, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${color}))` }}>{s.pct}%</span>
            <span style={{ ...Mono, fontSize: 11, letterSpacing: '0.1em', color: `hsl(var(${color}))` }}>{label}</span>
          </div>
          {s.count != null && <div style={{ ...Mono, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', marginTop: 8, letterSpacing: '0.04em' }}>{s.count} articles · past 7 days</div>}
        </div>
      )}
    </div>
  )
}

/* CandleChart now lives in ./CandleChart.jsx (lightweight-charts). The
   hand-rolled SVG implementation that used to sit here was removed
   2026-08-28 — see that file's header. Re-exported below so the V3 desktop
   TickerPanel's `import { CandleChart } from '@/v2/v3/TickerSheet'` keeps
   working. */
export { CandleChart }

function ChartSkeleton({ height = 224 }) {
  return <div className="atlas-skeleton" style={{ width: '100%', height, borderRadius: 'var(--studio-radius)' }} />
}
function Crossfade({ loading, skeleton, children }) {
  if (loading) return skeleton
  return <div style={{ animation: REDUCE ? 'none' : 'atlasFade 240ms ease' }}>{children}</div>
}

/* ── leveraged ETFs for this ticker (leveraged_etfs map + live 1D move) ──
   Reads /api/leveraged-etfs?underlying=TICKER, then /api/prices (snapshot)
   for the matching 2× ETF symbols so the section shows the real daily move.
   Renders nothing when the stock has no 2× ETF — same conditional pattern as
   the other Overview sections. */
function useLeveraged(ticker) {
  const [etfs, setEtfs] = useState([])
  const [prices, setPrices] = useState({})
  useEffect(() => {
    if (!ticker) { setEtfs([]); setPrices({}); return }
    let cancelled = false
    setEtfs([]); setPrices({})
    fetch(`/api/leveraged-etfs?underlying=${encodeURIComponent(ticker)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return
        const list = d?.instruments?.[0]?.etfs || []
        setEtfs(list)
        if (list.length) {
          fetch(`/api/prices?tickers=${list.map((e) => e.ticker).join(',')}&mode=snapshot`)
            .then((r) => r.ok ? r.json() : null)
            .then((pd) => { if (!cancelled && pd?.prices) setPrices(pd.prices) })
            .catch(() => {})
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [ticker])
  return { etfs, prices }
}

function LeveragedSection({ ticker }) {
  const { etfs, prices } = useLeveraged(ticker)
  if (!etfs.length) return null
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
        {Kk('Leveraged · 2× Daily')}
        <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-success))' }} />LIVE
        </span>
      </div>
      {etfs.map((e, i) => {
        const chg = prices[e.ticker]?.change1d
        const up = chg != null && chg >= 0
        const col = chg == null ? '--studio-text-subtle' : up ? '--studio-success' : '--studio-danger'
        return (
          <div key={e.ticker} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 2px', borderTop: i ? '1px solid hsl(var(--studio-border) / 0.5)' : 'none' }}>
            <span style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', minWidth: 52 }}>{e.ticker}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'hsl(var(--studio-text-muted))' }}>{e.issuer} {e.leverage}x Long {ticker}</div>
              {e.expense_ratio != null && <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>EXPENSE {e.expense_ratio}%</div>}
            </div>
            <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.06em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.3)', borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}>{e.leverage}×</span>
            <span style={{ ...Mono, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${col}))`, minWidth: 58, textAlign: 'right' }}>
              {chg == null ? '—' : `${up ? '+' : ''}${chg.toFixed(2)}%`}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/* ══════════════ TAB CONTENT ══════════════ */

// `perf` + `split` are built ONCE in the TickerSheet root and passed down, so
// the sheet header and this tab's PerfGrid are physically the same numbers —
// they used to be two maps and the header's lacked 5Y/MAX (audit finding F4).
function OverviewTab({ stock, live, fund, perf, split, tf, setTf, type, setType,
  indicators, setIndicators, gearOpen, setGearOpen, advOpen, setAdvOpen, scaleMode, setScaleMode, interval, setInterval,
  measure, setMeasure }) {
  const { bars, loading } = useBars(stock.ticker, tf, interval)
  // Lead-in only — never rendered as its own window, see CandleChart's
  // `warmup` prop. null range short-circuits the fetch.
  const { bars: warmup } = useBars(stock.ticker, WARMUP_TF[tf] || null)
  // Color the chart by the series it draws (desktop TickerPanel's rule) —
  // the perf column can cover a different window than the plotted bars, so
  // coloring by perf[tf] could paint a falling line green. perf is only the
  // fallback while bars haven't landed.
  const chartUp = (bars && bars.length > 1) ? bars[bars.length - 1].c >= bars[0].c : (perf[tf] ?? 0) >= 0

  const fundCells = [
    // Live Finnhub only — no fallback to the seed-era stock.rev/stock.pe
    // columns (stale since seed; a silent substitute hides a dead feed).
    ['Revenue Growth', fund?.revenueGrowth ?? null],
    ['P/E Ratio', fund?.pe ?? null],
    ['Net Margin', fund?.netMargin],
    ['ROE', fund?.roe],
    ['Beta', fund?.beta ?? null],
  ].filter(([, v]) => v)

  // Badge the gear with how many non-default indicators are on, so an
  // enabled RSI/MACD isn't invisible when the panel is shut.
  const activeCount = Object.entries({ ...DEFAULT_INDICATORS, ...indicators })
    .filter(([k, v]) => v && k !== 'volume').length
  // Usable bars in the current range — an indicator needing more than this
  // can't produce a value, so the panel greys it out instead of drawing
  // nothing (1M is ~22 daily bars, which silently killed MACD).
  // Availability is judged against the series indicators actually see —
  // the warm-up one when there is one.
  const indSrc = (warmup && bars && warmup.length > bars.length) ? warmup : bars
  const barCount = Array.isArray(indSrc) ? indSrc.filter((b) => b && b.c != null).length : 0

  const sentPct = stock.cron_sentiment_bullish_pct
  const sentiment = sentPct != null ? { pct: Math.round(sentPct), count: stock.cron_buzz_article_count } : null

  return (
    <div>
      {/* chart controls — timeframe chips left; chart-mode pill, settings gear
          and expand on the right. OPT stays a mode of the pill (Ben: no big
          button) — it swaps the chart area for the options chain. The gear
          opens the indicator panel; expand opens the zoomable advanced view
          (zoom is deliberately OFF on this inline chart, or pinch/pan fights
          the sheet's vertical scroll). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
        <div style={{ display: 'flex', gap: 10, minHeight: 22, overflowX: 'auto' }}>
          {type !== 'options' && TICKER_TF.map((x) => (
            <button key={x} onClick={() => setTf(x)} style={{ background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', flexShrink: 0, ...Mono, fontSize: 11, letterSpacing: '0.06em',
              color: tf === x ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))', borderBottom: tf === x ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent', transition: 'color 150ms' }}>{x}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, position: 'relative' }}>
          <div style={{ display: 'flex', borderRadius: 'var(--studio-radius-sm)', overflow: 'hidden', border: '1px solid hsl(var(--studio-border))' }}>
            {[['candles', '▦'], ['line', '〜'], ['options', 'OPT']].map(([t, label]) => (
              <button key={t} onClick={() => setType(t)} title={t} style={{ ...Mono, fontSize: 9.5, letterSpacing: t === 'options' ? '0.06em' : undefined, padding: '5px 7px', cursor: 'pointer', border: 'none',
                background: (type === t || (t === 'candles' && CANDLE_KINDS[type])) ? 'hsl(var(--studio-surface-2))' : 'transparent',
                color: (type === t || (t === 'candles' && CANDLE_KINDS[type])) ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))' }}>{label}</button>
            ))}
          </div>
          {type !== 'options' && (
            <>
              <button onClick={() => setGearOpen(!gearOpen)} aria-label="Chart settings" aria-expanded={gearOpen} title="Chart settings"
                style={{ all: 'unset', cursor: 'pointer', padding: '4px 3px', lineHeight: 1, ...Mono, fontSize: 12,
                  color: `hsl(var(--studio-${gearOpen || activeCount ? 'accent' : 'text-subtle'}))` }}>
                ⚙{activeCount ? <sup style={{ fontSize: 7.5, marginLeft: 1 }}>{activeCount}</sup> : null}
              </button>
              <button onClick={() => setMeasure(!measure)} aria-label="Measure" aria-pressed={measure} title="Measure"
                style={{ all: 'unset', cursor: 'pointer', padding: '4px 3px', lineHeight: 1, ...Mono, fontSize: 12,
                  color: `hsl(var(--studio-${measure ? 'accent' : 'text-subtle'}))` }}>⇔</button>
              <button onClick={() => setAdvOpen(true)} aria-label="Expand chart" title="Expand chart"
                style={{ all: 'unset', cursor: 'pointer', padding: '4px 3px', lineHeight: 1, ...Mono, fontSize: 12, color: 'hsl(var(--studio-text-subtle))' }}>⤢</button>
            </>
          )}
          {gearOpen && (
            <div style={{ position: 'absolute', top: 30, right: 0, zIndex: 40 }}>
              <ChartSettingsPanel type={type} setType={setType} indicators={indicators} setIndicators={setIndicators} onClose={() => setGearOpen(false)} barCount={barCount} scaleMode={scaleMode} setScaleMode={setScaleMode}
                tf={tf} interval={interval} setInterval={setInterval} />
            </div>
          )}
        </div>
      </div>
      {type === 'options' ? (
        <OptionsTab stock={stock} />
      ) : (
        <Crossfade loading={loading} skeleton={<ChartSkeleton height={224} />}>
          <CandleChart bars={bars} warmup={warmup} type={type} up={chartUp} height={224} tf={tf} indicators={indicators}
            scaleMode={scaleMode} measure={measure} />
        </Crossfade>
      )}
      {advOpen && (
        <AdvancedChart ticker={stock.ticker} bars={bars} warmup={warmup} tf={tf} setTf={setTf} timeframes={TICKER_TF}
          type={type === 'options' ? 'line' : type} setType={setType}
          indicators={indicators} setIndicators={setIndicators} up={chartUp} onClose={() => setAdvOpen(false)}
          scaleMode={scaleMode} setScaleMode={setScaleMode} interval={interval} setInterval={setInterval} />
      )}

      {/* price performance — real per-timeframe %. Directly under the chart
          (Ben, 2026-08-15): it reads as part of the chart, and burying it
          below the intel block pushed it off-screen. */}
      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
          {Kk('Price Performance')}
          {/* 1D–1Y are live snapshot buckets; 5Y/MAX come from price_history
              via /api/overnight-split and are as-of last night's close. This
              said "LIVE" for every timeframe, which was wrong on exactly the
              two that aren't (audit finding F5). */}
          <span title={IS_EOD_TF[tf] && split.asOf ? `As of the ${split.asOf} close` : undefined}
            style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>
            ● {tf} {IS_EOD_TF[tf] ? 'EOD' : 'LIVE'}
          </span>
        </div>
        <PerfGrid perf={perf} tf={tf} onPick={setTf} />
      </div>

      {/* Atlas Intel — the nightly analysis (thesis → latest analysis →
          risk to thesis → notes), after the price block. */}
      <IntelSection stock={stock} />

      {/* descriptions — hide entirely when null */}
      {(stock.what_they_do || stock.tidbit) && (
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column' }}>
          {stock.what_they_do && <Disclosure label="What They Do" body={stock.what_they_do} />}
          {stock.tidbit && <Disclosure label="Did You Know" body={stock.tidbit} />}
        </div>
      )}

      {/* key stats — live fundamentals */}
      {fundCells.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
            {Kk('Key Stats')}
            {fund && <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-success))', display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 5, height: 5, borderRadius: '50%', background: 'hsl(var(--studio-success))' }} />LIVE</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            {fundCells.map(([k, v], i) => <StatPill key={k} label={k.toUpperCase()} value={v} index={i} total={fundCells.length} />)}
          </div>
        </div>
      )}

      {/* news sentiment */}
      {sentiment && (
        <div style={{ marginTop: 14 }}><Sentiment s={sentiment} /></div>
      )}

      {/* leveraged 2× ETFs — renders only when the stock has one. LAST on the
          tab by design: it's a niche companion instrument, not part of reading
          the underlying, so it sits below the company/fundamentals block rather
          than splitting Price Performance from What They Do. */}
      <LeveragedSection ticker={stock.ticker} />
    </div>
  )
}

// Atlas Intel — the nightly AI analysis + thesis/risk/notes. Lives INLINE on the
// Overview tab (it's the most important content, so it shouldn't hide behind a
// near-empty tab). Renders only the sections that have data; when there's no
// coverage yet it shows a compact one-liner instead of a whole-tab placeholder.
function IntelSection({ stock }) {
  // Paused (Ben, 2026-08-29) — render nothing at all rather than an
  // explanatory placeholder. An empty state is still a block of chrome about a
  // feature that isn't there; the sheet reads better with the price data
  // running straight into the descriptions.
  if (ANALYSIS_PAUSED) return null
  const cron_summary = stock.cron_summary
  const thesisLong = stock.thesis
  const hasThesis = thesisLong && !isEnumThesis(thesisLong)
  const hasAny = cron_summary || hasThesis || stock.thesis_risk || stock.notes
  if (!hasAny) {
    return (
      <div style={{ marginTop: 22 }}>
        {Kk('Atlas Intel')}
        <p style={{ fontSize: 12, lineHeight: 1.6, color: 'hsl(var(--studio-text-subtle))', margin: '9px 0 0' }}>
          {stock.tier === 'hunting'
            ? <>Hunting tier — the daily AI summary covers <b style={{ ...Mono, color: 'hsl(var(--studio-text))' }}>{stock.ticker}</b>, but the first run is still pending.</>
            : <>The nightly Atlas analysis posts around 5:30 PM CT on weekdays.</>}
        </p>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 22 }}>
      {/* thesis — real (stock.thesis); the prescriptive rating/conviction
          "Atlas Opinion" block was removed (Ben: no buy/sell verdict). */}
      {hasThesis && (
        <div style={{ background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', padding: 18 }}>
          {Kk('Thesis')}
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', margin: '10px 0 0' }}>{thesisLong}</p>
        </div>
      )}

      {/* atlas latest analysis (cron_summary) — the headline intel */}
      {cron_summary && (
        <div style={{ marginTop: hasThesis ? 16 : 0, background: 'hsl(var(--studio-surface) / 0.55)', border: '1px solid hsl(var(--studio-border))', borderLeftWidth: 2, borderLeftColor: 'hsl(var(--studio-accent))', borderRadius: 'var(--studio-radius)', padding: '15px 16px' }}>
          {Kk('Atlas · Latest Analysis')}
          <p style={{ fontSize: 12.5, lineHeight: 1.62, color: 'hsl(var(--studio-text-muted))', margin: '9px 0 0' }}>{cron_summary}</p>
          {stock.last_analyzed && <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 11 }}>UPDATED {relTime(stock.last_analyzed).toUpperCase()}</div>}
        </div>
      )}

      {/* risk to thesis */}
      {stock.thesis_risk && (
        <div style={{ marginTop: 16 }}>
          {Kk('Risk to Thesis', 'hsl(var(--studio-danger))')}
          <p style={{ fontSize: 12.5, lineHeight: 1.62, color: 'hsl(var(--studio-text-muted))', margin: '9px 0 0' }}>{stock.thesis_risk}</p>
        </div>
      )}

      {/* notes */}
      {stock.notes && (
        <div style={{ marginTop: 16, padding: '11px 13px', background: 'hsl(var(--studio-surface) / 0.4)', border: '1px dashed hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)' }}>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 6 }}>NOTES</div>
          <p style={{ fontSize: 11.5, lineHeight: 1.55, color: 'hsl(var(--studio-text-subtle))', margin: 0, fontStyle: 'italic' }}>{stock.notes}</p>
        </div>
      )}
    </div>
  )
}

/* catalyst type → visual config */
const CAT_CFG = {
  earnings: { label: 'Earnings', color: '--studio-accent' },
  analyst: { label: 'Analyst Day', color: '--studio-accent-soft' },
  rebalance: { label: 'Rebalance', color: '--studio-text-subtle' },
  macro: { label: 'Macro', color: '--studio-accent' },
  product: { label: 'Product', color: '--studio-success' },
}
function daysUntil(dateStr) {
  const d = Math.round((new Date(dateStr) - Date.now()) / 864e5)
  if (d < 0) return null
  if (d === 0) return 'today'
  if (d === 1) return 'tomorrow'
  if (d < 14) return `in ${d}d`
  if (d < 60) return `in ${Math.round(d / 7)}w`
  return `in ${Math.round(d / 30)}mo`
}
function fmtCatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()
}

/* ══════════════ Options tab — near-the-money chain + unusual flow ══════════════
   Reads /api/options-flow (Nasdaq chain — the same source as the
   options_unusual signal ingest). Lazy: fetched only when the tab is opened,
   then auto-refreshes every 3 minutes while it stays mounted — Nasdaq is a
   free unofficial feed and the endpoint sits behind a 120s edge cache, so all
   users share roughly one upstream call per cycle. Quotes may be delayed;
   the stamp row is honest about when the snapshot was fetched. */
const OPTIONS_TTL_MS = 180_000
function useOptionsFlow(ticker) {
  const [data, setData] = useState(null)
  const [state, setState] = useState('loading') // loading | ok | error
  const [fetchedAt, setFetchedAt] = useState(null)
  useEffect(() => {
    if (!ticker) return
    let cancelled = false
    let last = 0
    const load = async () => {
      last = Date.now()
      try {
        const r = await fetch(`/api/options-flow?ticker=${encodeURIComponent(ticker)}`)
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const j = await r.json()
        if (cancelled) return
        setData(j); setState('ok'); setFetchedAt(Date.now())
      } catch {
        if (!cancelled) setState((prev) => (prev === 'ok' ? 'ok' : 'error'))
      }
    }
    setData(null); setState('loading'); setFetchedAt(null)
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, OPTIONS_TTL_MS)
    // revalidate-on-foreground (CLAUDE.md polling rule), throttled to the TTL
    const reval = () => { if (document.visibilityState === 'visible' && Date.now() - last > OPTIONS_TTL_MS) load() }
    document.addEventListener('visibilitychange', reval)
    window.addEventListener('focus', reval)
    return () => {
      cancelled = true; clearInterval(id)
      document.removeEventListener('visibilitychange', reval)
      window.removeEventListener('focus', reval)
    }
  }, [ticker])
  return { data, state, fetchedAt }
}

const fmtQ = (v) => (v == null || v === 0 ? '—' : v.toFixed(2))
const fmtVol = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v))
const fmtPrem = (v) => (v == null ? '—' : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1e3)}K`)
const fmtExpShort = (iso) => {
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? String(iso || '') : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Does any strike in the chain carry computed greeks? Drives the "tap for
// greeks" hint + the model caption — both hidden when the source gave us none.
const hasGreeksAny = (chain) => chain.some((e) => e.rows?.some((r) => r.call?.greeks || r.put?.greeks))

// One side's greeks (call or put) as a labeled mono column. Only the fields the
// model produced render; a missing greek shows "—". IV/Δ/Γ/Θ/V, Robinhood-style.
function GreeksCol({ g, label, color, sub }) {
  const val = { ...Mono, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text))' }
  const rows = g ? [
    ['IV', g.iv == null ? '—' : `${g.iv}%`],
    ['Δ (delta)', g.delta == null ? '—' : g.delta.toFixed(3)],
    ['Γ (gamma)', g.gamma == null ? '—' : g.gamma.toFixed(4)],
    ['Θ (theta)', g.theta == null ? '—' : g.theta.toFixed(3)],
    ['V (vega)', g.vega == null ? '—' : g.vega.toFixed(3)],
  ] : null
  return (
    <div>
      <div style={{ ...sub, color: `hsl(var(${color}))`, marginBottom: 5 }}>{label}</div>
      {rows ? rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
          <span style={sub}>{k}</span>
          <span style={val}>{v}</span>
        </div>
      )) : <div style={{ ...sub, opacity: 0.6 }}>—</div>}
    </div>
  )
}

export function OptionsTab({ stock }) {
  const { data, state, fetchedAt } = useOptionsFlow(stock.ticker)
  const [expIdx, setExpIdx] = useState(0)
  const [openStrike, setOpenStrike] = useState(null)
  const cell = { ...Mono, fontSize: 11, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }
  const sub = { ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }

  if (state === 'loading') {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[64, 220, 140].map((h, i) => <div key={i} className="atlas-skeleton" style={{ height: h, borderRadius: 'var(--studio-radius)' }} />)}</div>
  }
  if (state === 'error' || !data) {
    return (
      <div style={{ padding: '30px 0', textAlign: 'center' }}>
        {Kk('Options')}
        <p style={{ fontSize: 12, lineHeight: 1.6, color: 'hsl(var(--studio-text-subtle))', margin: '10px 0 0' }}>
          The options chain source didn&apos;t answer. It usually recovers on its own &mdash; reopen this tab in a minute.
        </p>
      </div>
    )
  }

  const t = data.totals || {}
  const pcr = t.put_call_volume_ratio
  const chain = Array.isArray(data.chain) ? data.chain : []
  const exp = chain[Math.min(expIdx, Math.max(0, chain.length - 1))]
  const unusual = Array.isArray(data.unusual) ? data.unusual.slice(0, 6) : []

  return (
    <div>
      {/* stamp — honest about freshness: a periodic snapshot, possibly delayed */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {Kk('Options · Chain Snapshot')}
        <span style={sub}>
          {fetchedAt ? `AS OF ${new Date(fetchedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase()} · ` : ''}REFRESHES ~3 MIN
        </span>
      </div>

      {/* totals band */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, marginBottom: 16 }}>
        {[
          ['CALL VOL', fmtVol(t.call_volume), '--studio-success'],
          ['PUT VOL', fmtVol(t.put_volume), '--studio-danger'],
          ['PUT/CALL', pcr == null ? '—' : pcr.toFixed(2), pcr == null ? '--studio-text' : pcr < 0.7 ? '--studio-success' : pcr > 1.3 ? '--studio-danger' : '--studio-text'],
        ].map(([k, v, c], i) => (
          <div key={k} style={{ padding: '4px 0 4px 12px', borderLeft: i ? '1px solid hsl(var(--studio-border) / 0.5)' : 'none' }}>
            <div style={{ ...Mono, fontSize: 15, color: `hsl(var(${c}))`, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
            <div style={{ ...sub, marginTop: 4 }}>{k}</div>
          </div>
        ))}
      </div>

      {/* chain browser — expiry chips + near-the-money T-table */}
      {chain.length > 0 && exp && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 9, overflowX: 'auto' }}>
            {chain.map((c, i) => (
              <button key={c.expiration} onClick={() => { setExpIdx(i); setOpenStrike(null) }}
                style={{ background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', flexShrink: 0, ...Mono, fontSize: 10.5, letterSpacing: '0.06em',
                  color: i === expIdx ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))',
                  borderBottom: i === expIdx ? '1.5px solid hsl(var(--studio-accent))' : '1.5px solid transparent' }}>
                {fmtExpShort(c.expiration).toUpperCase()}
              </button>
            ))}
          </div>
          {/* Column order follows the broker convention: the tradeable quote
              (bid/ask) flanks the strike, volume sits on the outer edges, and
              the strike is the centered spine of the table. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 58px 1fr 1fr 1fr', gap: '0 6px', padding: '5px 0', borderBottom: '1px solid hsl(var(--studio-border) / 0.6)' }}>
            {['VOL', 'BID', 'ASK', 'STRIKE', 'BID', 'ASK', 'VOL'].map((h, i) => (
              <span key={i} style={{ ...sub, textAlign: i === 3 ? 'center' : i < 3 ? 'right' : 'left' }}>{h}</span>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 2px 0' }}>
            <span style={{ ...sub, color: 'hsl(var(--studio-success))' }}>CALLS</span>
            <span style={{ ...sub, color: 'hsl(var(--studio-danger))' }}>PUTS</span>
          </div>
          {exp.rows.map((r) => {
            const atm = data.price > 0 && Math.abs(r.strike - data.price) / data.price < 0.015
            const isOpen = openStrike === r.strike
            const hasGreeks = r.call?.greeks || r.put?.greeks
            return (
              <div key={r.strike}>
                <div onClick={() => hasGreeks && setOpenStrike(isOpen ? null : r.strike)}
                  role={hasGreeks ? 'button' : undefined} tabIndex={hasGreeks ? 0 : undefined}
                  onKeyDown={hasGreeks ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenStrike(isOpen ? null : r.strike) } } : undefined}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 58px 1fr 1fr 1fr', gap: '0 6px', padding: '6px 0',
                    borderBottom: '1px solid hsl(var(--studio-border) / 0.35)', cursor: hasGreeks ? 'pointer' : 'default',
                    background: isOpen ? 'hsl(var(--studio-surface) / 0.9)' : atm ? 'hsl(var(--studio-surface) / 0.6)' : 'transparent' }}>
                  <span style={{ ...cell, color: 'hsl(var(--studio-text-subtle))' }}>{r.call ? fmtVol(r.call.volume) : '—'}</span>
                  <span style={cell}>{fmtQ(r.call?.bid)}</span>
                  <span style={cell}>{fmtQ(r.call?.ask)}</span>
                  <span style={{ ...cell, textAlign: 'center', letterSpacing: '0.02em',
                    color: atm ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))' }}>{r.strike}</span>
                  <span style={{ ...cell, textAlign: 'left' }}>{fmtQ(r.put?.bid)}</span>
                  <span style={{ ...cell, textAlign: 'left' }}>{fmtQ(r.put?.ask)}</span>
                  <span style={{ ...cell, textAlign: 'left', color: 'hsl(var(--studio-text-subtle))' }}>{r.put ? fmtVol(r.put.volume) : '—'}</span>
                </div>
                {isOpen && hasGreeks && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px', padding: '9px 2px 11px',
                    borderBottom: '1px solid hsl(var(--studio-border) / 0.35)', background: 'hsl(var(--studio-surface) / 0.4)' }}>
                    <GreeksCol g={r.call?.greeks} label="CALL" color="--studio-success" sub={sub} />
                    <GreeksCol g={r.put?.greeks} label="PUT" color="--studio-danger" sub={sub} />
                  </div>
                )}
              </div>
            )
          })}
          <div style={{ ...sub, marginTop: 7 }}>
            {hasGreeksAny(chain) ? 'TAP A STRIKE FOR GREEKS · ' : ''}{data.price > 0 ? `STRIKES NEAREST $${Math.round(data.price)} · ` : ''}{String(data.source || '').toUpperCase()} · QUOTES MAY BE DELAYED
          </div>
          {hasGreeksAny(chain) && (
            <div style={{ ...sub, marginTop: 3, opacity: 0.7 }}>GREEKS COMPUTED · BLACK-SCHOLES · IV FROM MID</div>
          )}
        </div>
      )}

      {/* unusual flow — same bar the signal scanners use (vol ≥ 2× OI, real size) */}
      {unusual.length > 0 && (
        <div>
          <div style={{ marginBottom: 9 }}>{Kk('Unusual Flow')}</div>
          {unusual.map((u) => (
            <div key={u.contract} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid hsl(var(--studio-border) / 0.35)' }}>
              <span style={{ ...Mono, fontSize: 9, letterSpacing: '0.08em', padding: '2px 6px', borderRadius: 'var(--studio-radius-sm)', flexShrink: 0,
                color: `hsl(var(${u.type === 'call' ? '--studio-success' : '--studio-danger'}))`,
                border: `1px solid hsl(var(${u.type === 'call' ? '--studio-success' : '--studio-danger'}) / 0.4)` }}>
                {u.type === 'call' ? 'CALL' : 'PUT'}
              </span>
              <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-text))', flex: 1 }}>${u.strike} · {fmtExpShort(u.expiration)}</span>
              <span style={{ ...Mono, fontSize: 10.5, color: 'hsl(var(--studio-text-subtle))' }}>{fmtVol(u.volume)} vol / {fmtVol(u.open_interest)} OI</span>
              <span style={{ ...Mono, fontSize: 11.5, color: 'hsl(var(--studio-text))', minWidth: 52, textAlign: 'right' }}>{fmtPrem(u.premium)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


function OutlookTab({ stock }) {
  const { earnings } = useEarnings()
  // Next earnings for this ticker, real, from the Atlas earnings calendar.
  const next = useMemo(() => {
    const rows = (earnings || []).filter((e) => e.ticker === stock.ticker)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
    return rows[0] || null
  }, [earnings, stock.ticker])
  const daysOut = next ? Math.max(0, Math.round((new Date(next.date) - Date.now()) / 864e5)) : null
  const session = next ? (/before|bmo/i.test(next.time) ? 'BMO' : /after|amc/i.test(next.time) ? 'AMC' : '') : ''

  // Catalysts beyond earnings have no single real source — seed the earnings
  // row (real date) into a catalyst list. TODO(data): wire analyst-day /
  // product / macro catalysts when a source exists (calendar.js exposes only
  // earnings + macro at the index level, not per-ticker product events).
  const catalysts = useMemo(() => {
    const list = []
    if (next) list.push({ date: next.date, type: 'earnings', note: `${next.quarter ? next.quarter + ' ' : ''}earnings${session ? ' · ' + (session === 'AMC' ? 'after close' : 'before open') : ''}` })
    return list.sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [next, session])

  return (
    <div>
      {/* neutral surface, hairline border — amber stays a line/label here,
          never a fill (v3 rule); the accent-soft day-count + date carry it */}
      {next && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', marginBottom: 18,
          background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)' }}>
          <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 44 }}>
            <div style={{ ...Mono, fontSize: 19, color: 'hsl(var(--studio-accent-soft))', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{daysOut}</div>
            <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>DAYS</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>Earnings{session ? ` · ${session === 'AMC' ? 'after close' : 'before open'}` : ''}</div>
            <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>
              {fmtCatDate(next.date)} · FROM THE ATLAS CALENDAR
            </div>
          </div>
          {session && (
            <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)', flexShrink: 0,
              background: 'hsl(var(--studio-accent) / 0.12)', border: '1px solid hsl(var(--studio-accent) / 0.3)', color: 'hsl(var(--studio-accent-soft))' }}>{session}</div>
          )}
        </div>
      )}
      {Kk('Upcoming Catalysts')}
      {catalysts.length > 0 ? (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column' }}>
          {catalysts.map((c, i) => {
            const cfg = CAT_CFG[c.type] || CAT_CFG.rebalance
            const rel = daysUntil(c.date)
            const isPast = rel === null
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '58px 1fr auto', alignItems: 'start', gap: 12, padding: '13px 0',
                borderBottom: i < catalysts.length - 1 ? '1px solid hsl(var(--studio-border) / 0.45)' : 'none', opacity: isPast ? 0.45 : 1 }}>
                <div>
                  <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.04em', color: isPast ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${cfg.color}))`, fontVariantNumeric: 'tabular-nums' }}>{fmtCatDate(c.date)}</div>
                  {rel && <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 3 }}>{rel}</div>}
                </div>
                <div style={{ fontSize: 12.5, color: 'hsl(var(--studio-text-muted))', lineHeight: 1.45 }}>{c.note}</div>
                <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)',
                  background: `hsl(var(${cfg.color}) / 0.12)`, border: `1px solid hsl(var(${cfg.color}) / 0.3)`, color: `hsl(var(${cfg.color}))`, whiteSpace: 'nowrap', marginTop: 1 }}>{cfg.label.toUpperCase()}</div>
              </div>
            )
          })}
        </div>
      ) : (
        <p style={{ fontSize: 12.5, lineHeight: 1.6, color: 'hsl(var(--studio-text-subtle))', margin: '14px 0 0' }}>
          No tracked catalysts for {stock.ticker} yet. Earnings dates populate Sundays from the Atlas calendar.
        </p>
      )}
      <div style={{ marginTop: 16, padding: '11px 13px', background: 'hsl(var(--studio-surface) / 0.4)', border: '1px dashed hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)' }}>
        <p style={{ ...Mono, fontSize: 10, lineHeight: 1.6, letterSpacing: '0.02em', color: 'hsl(var(--studio-text-subtle))', margin: 0 }}>Calendar reflects Atlas&apos;s tracked events. Earnings and analyst dates confirm closer to the window.</p>
      </div>
    </div>
  )
}

/* ══════════════ EARNINGS ══════════════ */

/* Verbatim call bodies come from a SCRAPE of Yahoo Finance (see the
   earnings_transcripts migration), not licensed data. Rendering them to the
   public web is the one part of this pipeline on soft ground, so it has a kill
   switch. Default ON — the tab is useless without the text — set
   VITE_SHOW_TRANSCRIPT_BODY=false to fall back to metadata + speakers only. */
const SHOW_TRANSCRIPT_BODY = import.meta.env.VITE_SHOW_TRANSCRIPT_BODY !== 'false'

/* Financials arrive in ACTUAL DOLLARS (verified against Credo's 8-K: revenue
   comes through as 437003000 for a $437.0M quarter), so scale on read. */
function fmtMoney(v) {
  if (v == null || !isFinite(v)) return '—'
  const a = Math.abs(v), sign = v < 0 ? '−' : ''
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`
  return `${sign}$${a.toFixed(0)}`
}
function marginPct(part, whole) {
  if (part == null || !whole) return null
  return (100 * part) / whole
}
/* Y/Y needs the SAME fiscal quarter one year back, matched on (fy-1, fq) —
   never "four rows down", which silently compares the wrong periods whenever a
   quarter is missing from the list. */
function yoyPct(cur, prior) {
  if (cur == null || prior == null || prior === 0) return null
  return (100 * (cur - prior)) / Math.abs(prior)
}

/* One metric line in the expanded detail: label, value, optional secondary
   (margin or Y/Y). Hairline rows, not tiles — v3 rule. */
function MetricRow({ label, value, note, noteTone }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '7px 0',
      borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'hsl(var(--studio-text-subtle))', flex: 1, minWidth: 0 }}>{label}</div>
      {note != null && (
        <div style={{ ...Mono, fontSize: 9.5, fontVariantNumeric: 'tabular-nums',
          color: noteTone ? `hsl(var(${noteTone}))` : 'hsl(var(--studio-text-subtle))' }}>{note}</div>
      )}
      <div style={{ ...Mono, fontSize: 12, fontVariantNumeric: 'tabular-nums',
        color: 'hsl(var(--studio-text))', minWidth: 74, textAlign: 'right' }}>{value}</div>
    </div>
  )
}

/* The reported income statement for one quarter. GAAP as-reported — it will
   NOT match the non-GAAP number a company leads its press release with, which
   is worth saying on screen rather than leaving people to wonder. */
function FinancialsBlock({ fin, prior }) {
  if (!fin) {
    return (
      <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))',
        padding: '10px 0 4px' }}>
        NUMBERS NOT PUBLISHED YET · TRANSCRIPT ONLY
      </div>
    )
  }
  const gm = marginPct(fin.gross_profit, fin.revenue)
  const om = marginPct(fin.operating_income, fin.revenue)
  const nm = marginPct(fin.net_income, fin.revenue)
  const revYoY = yoyPct(fin.revenue, prior?.revenue)
  const epsYoY = yoyPct(fin.eps_diluted, prior?.eps_diluted)
  const tone = (v) => (v == null ? null : v >= 0 ? '--studio-success' : '--studio-danger')
  const pct1 = (v) => (v == null ? null : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`)

  return (
    <div style={{ padding: '6px 0 14px' }}>
      <MetricRow label="Revenue" value={fmtMoney(fin.revenue)}
        note={pct1(revYoY) && `${pct1(revYoY)} Y/Y`} noteTone={tone(revYoY)} />
      <MetricRow label="Gross profit" value={fmtMoney(fin.gross_profit)}
        note={gm == null ? null : `${gm.toFixed(1)}% GM`} />
      <MetricRow label="Operating income" value={fmtMoney(fin.operating_income)}
        note={om == null ? null : `${om.toFixed(1)}% OM`} />
      <MetricRow label="Net income" value={fmtMoney(fin.net_income)}
        note={nm == null ? null : `${nm.toFixed(1)}% NM`} />
      <MetricRow label="EPS (diluted)"
        value={fin.eps_diluted == null ? '—' : `$${Number(fin.eps_diluted).toFixed(2)}`}
        note={pct1(epsYoY) && `${pct1(epsYoY)} Y/Y`} noteTone={tone(epsYoY)} />
      {fin.ebitda != null && <MetricRow label="EBITDA" value={fmtMoney(fin.ebitda)} />}
      {fin.rnd != null && <MetricRow label="R&D" value={fmtMoney(fin.rnd)}
        note={marginPct(fin.rnd, fin.revenue) == null ? null : `${marginPct(fin.rnd, fin.revenue).toFixed(1)}% of rev`} />}
      {fin.sga != null && <MetricRow label="SG&A" value={fmtMoney(fin.sga)} />}
      <div style={{ ...Mono, fontSize: 8, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))',
        borderTop: '1px solid hsl(var(--studio-border) / 0.4)', paddingTop: 9, marginTop: 2 }}>
        GAAP AS REPORTED · PERIOD ENDING {String(fin.period_end || '').toUpperCase()}
      </div>
    </div>
  )
}

/* One expanded quarter: the numbers, then the call. Fetches its own body so
   the quarter list stays metadata-only — bodies average ~44 KB and eight of
   them would be a ~350 KB payload to draw eight rows. */
function QuarterDetail({ ticker, fy, fq, prior, highlightN }) {
  const { quarter, loading } = useEarningsQuarter(ticker, fy, fq)

  // A citation from AskCalls names a paragraph; once the body is in, bring
  // it into view. The paragraph carries an accent stroke while highlighted
  // (a line, never a fill — see the v3 amber rule).
  useEffect(() => {
    if (loading || highlightN == null) return
    const el = document.getElementById(`tx-${fy}-${fq}-${highlightN}`)
    el?.scrollIntoView({ block: 'center', behavior: REDUCE ? 'auto' : 'smooth' })
  }, [loading, highlightN, fy, fq])

  if (loading) {
    return <div style={{ padding: '12px 0 16px' }}>{[0, 1, 2].map((i) => <SkeletonRow key={i} i={i} />)}</div>
  }
  if (!quarter) {
    return (
      <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', padding: '12px 0 16px' }}>
        QUARTER UNAVAILABLE
      </div>
    )
  }

  const paras = quarter.transcript?.paragraphs || []
  return (
    <div style={{ paddingBottom: 18 }}>
      <FinancialsBlock fin={quarter.financials} prior={prior} />

      {paras.length === 0 ? (
        <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', paddingTop: 4 }}>
          NO CALL TRANSCRIPT FOR THIS QUARTER
        </div>
      ) : !SHOW_TRANSCRIPT_BODY ? (
        <div style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', lineHeight: 1.6, paddingTop: 4 }}>
          {quarter.transcript.paragraph_count} paragraphs · {(quarter.transcript.speakers || []).length} speakers.
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 6 }}>
            BODY HIDDEN BY CONFIGURATION
          </div>
        </div>
      ) : (
        <>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))',
            borderTop: '1px solid hsl(var(--studio-border) / 0.6)', paddingTop: 12, marginBottom: 10 }}>
            EARNINGS CALL
          </div>
          {paras.map((p, i) => {
            // Merge the speaker label out of consecutive paragraphs by the same
            // person so a long monologue reads as prose, not a name per line.
            const sameAsPrev = i > 0 && paras[i - 1].speaker === p.speaker
            const hl = highlightN != null && p.n === highlightN
            return (
              <div key={p.n ?? i} id={`tx-${fy}-${fq}-${p.n}`}
                style={{ marginBottom: 12, ...(hl ? { borderLeft: '2px solid hsl(var(--studio-accent))', paddingLeft: 10, marginLeft: -12 } : {}) }}>
                {!sameAsPrev && (
                  <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'hsl(var(--studio-accent-soft))', marginBottom: 4 }}>{p.speaker || '—'}</div>
                )}
                <div style={{ fontSize: 12.5, lineHeight: 1.65, color: 'hsl(var(--studio-text-muted))' }}>{p.text}</div>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}

/* Quarter list → tap a quarter → its numbers and its call, in place. */
function EarningsTab({ stock }) {
  const { quarters, loading } = useEarningsHistory(stock?.ticker)
  const [openKey, setOpenKey] = useState(null)
  // { fy, fq, n } of the paragraph a citation chip pointed at.
  const [highlight, setHighlight] = useState(null)

  // Year-ago same-quarter lookup for Y/Y, keyed on (fy, fq) so a gap in the
  // list can never shift the comparison onto the wrong period.
  const priorByKey = useMemo(() => {
    const byFyFq = new Map()
    for (const q of quarters) {
      if (q.fiscal_year != null && q.fiscal_quarter != null) {
        byFyFq.set(`${q.fiscal_year}-${q.fiscal_quarter}`, q.financials)
      }
    }
    const out = new Map()
    for (const q of quarters) {
      if (q.fiscal_year == null) continue
      out.set(`${q.fiscal_year}-${q.fiscal_quarter}`,
        byFyFq.get(`${q.fiscal_year - 1}-${q.fiscal_quarter}`) || null)
    }
    return out
  }, [quarters])

  if (loading) return <div>{[0, 1, 2, 3].map((i) => <SkeletonRow key={i} i={i} />)}</div>

  // No invented rows. The upstream is a scrape with real gaps (95% of the
  // universe is covered, not 100%), so an empty list is a legitimate answer
  // and says so rather than showing placeholder quarters.
  if (!quarters.length) {
    return (
      <div style={{ padding: '26px 0', textAlign: 'center' }}>
        <div style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>
          NO EARNINGS ON FILE
        </div>
        <div style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', marginTop: 8, lineHeight: 1.6 }}>
          Atlas keeps eight quarters for the tradeable universe. Coverage is about
          95% of names — this one has nothing stored yet.
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 10 }}>
        {quarters.length} {quarters.length === 1 ? 'QUARTER' : 'QUARTERS'} ON FILE
      </div>

      <AskCalls
        ticker={stock.ticker}
        onCite={(c) => {
          const q = quarters.find((x) => x.fiscal_year === c.fiscal_year && x.fiscal_quarter === c.fiscal_quarter)
          if (!q) return
          setOpenKey(`${q.fiscal_year}-${q.fiscal_quarter}-${q.report_date}`)
          setHighlight({ fy: c.fiscal_year, fq: c.fiscal_quarter, n: c.n_start })
        }}
      />

      {quarters.map((qtr) => {
        const key = `${qtr.fiscal_year}-${qtr.fiscal_quarter}-${qtr.report_date}`
        const open = openKey === key
        const fin = qtr.financials
        const prior = priorByKey.get(`${qtr.fiscal_year}-${qtr.fiscal_quarter}`) || null
        const revYoY = yoyPct(fin?.revenue, prior?.revenue)
        const label = qtr.fiscal_year != null
          ? `FY${qtr.fiscal_year} Q${qtr.fiscal_quarter}`
          : fmtCatDate(qtr.report_date)
        return (
          <div key={key} style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.6)' }}>
            <button
              onClick={() => setOpenKey(open ? null : key)}
              aria-expanded={open}
              disabled={qtr.fiscal_year == null}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                background: 'none', border: 'none', padding: '13px 0',
                cursor: qtr.fiscal_year == null ? 'default' : 'pointer' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...Mono, fontSize: 12.5, letterSpacing: '0.04em',
                  color: open ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))' }}>{label}</div>
                <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', marginTop: 4,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fmtCatDate(qtr.report_date)}
                  {qtr.transcript ? ` · ${(qtr.transcript.speakers || []).length} SPEAKERS` : ' · NO CALL'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ ...Mono, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text))' }}>
                  {fmtMoney(fin?.revenue)}
                </div>
                {revYoY != null && (
                  <div style={{ ...Mono, fontSize: 9, fontVariantNumeric: 'tabular-nums', marginTop: 3,
                    color: `hsl(var(${revYoY >= 0 ? '--studio-success' : '--studio-danger'}))` }}>
                    {revYoY >= 0 ? '+' : ''}{revYoY.toFixed(1)}% Y/Y
                  </div>
                )}
              </div>
              <div aria-hidden style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', flexShrink: 0,
                transform: open ? 'rotate(90deg)' : 'none', transition: REDUCE ? 'none' : 'transform 160ms' }}>›</div>
            </button>
            {open && (
              <QuarterDetail ticker={stock.ticker} fy={qtr.fiscal_year} fq={qtr.fiscal_quarter} prior={prior}
                highlightN={highlight && highlight.fy === qtr.fiscal_year && highlight.fq === qtr.fiscal_quarter ? highlight.n : null} />
            )}
          </div>
        )
      })}

      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))',
        borderTop: '1px solid hsl(var(--studio-border) / 0.6)', paddingTop: 12, marginTop: 4 }}>
        REPORTED FIGURES + CALLS VIA YAHOO FINANCE · UPDATED DAILY
      </div>
    </div>
  )
}

/* ══════════════ SHEET ══════════════ */
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'earnings', label: 'Earnings' },
  { id: 'outlook', label: 'Outlook' },
]
// stock_updates.alert_level vocabulary is urgent/warning/info/none (see the
// nightly cron prompt); legacy red/yellow/green kept in case old rows linger.
// 'none'/null/unknown → no badge (handled at the render gate).
const ALERT_TOKEN = {
  urgent: '--studio-danger', warning: '--studio-warning', info: '--studio-text-muted',
  red: '--studio-danger', yellow: '--studio-warning', green: '--studio-success',
}

export function TickerSheet({ stock, livePrices = {}, onClose }) {
  const live = (stock && livePrices[stock.ticker]) || {}
  const fund = useFundamentals(stock?.ticker)
  // /api/overnight-split is read for its per-window TOTAL only. The
  // overnight-vs-session breakdown it also returns was rendered under PerfGrid
  // as a two-row N/D grid and removed 2026-08-12 — it read as clutter next to
  // the live numbers and the N/D labels weren't self-explanatory. Don't
  // reinstate it without a clearer design; the endpoint and hook stay because
  // 5Y/MAX depend on them.
  //
  // Read HERE, at the root, rather than inside OverviewTab: the header % needs
  // the same 5Y/MAX numbers the grid shows, and two hook calls would be two
  // fetches of one URL.
  const split = useOvernightSplit(stock?.ticker)
  const [tf, setTf] = useState('1D')
  const [type, setType] = useState('line') // chart opens as the line chart, not candles
  // Indicator selection persists across sessions/devices (server-side for
  // signed-in users, localStorage otherwise) — retoggling RSI on every
  // ticker would be miserable.
  const [indicators, setIndicators] = useUserPref('chart_indicators', DEFAULT_INDICATORS)
  const [scaleMode, setScaleMode] = useUserPref('chart_scale_mode', 'normal')
  // Intraday bar resolution (1D/5D only). Default 5m = today's behaviour.
  const [interval, setInterval] = useUserPref('chart_interval', '5m')
  const [gearOpen, setGearOpen] = useState(false)
  const [advOpen, setAdvOpen] = useState(false)
  const [measure, setMeasure] = useState(false)
  const [tab, setTab] = useState('overview')
  const [dragY, setDragY] = useState(0) // px the sheet is pulled down (0 = resting)
  const [dragging, setDragging] = useState(false)
  const [shown, setShown] = useState(false)
  const [toast, setToast] = useState(null)
  const dragRef = useRef(null), overlayRef = useRef(null), bodyRef = useRef(null)

  // ── watchlist wiring (Clerk-backed; replaces the mockup's localStorage star).
  // "watched" = ticker present in any list; toggle acts on the active/first list.
  const { watchlists, active, addTicker, removeTicker } = useWatchlists()
  const ticker = stock?.ticker
  const watched = useMemo(
    () => !!ticker && (watchlists || []).some((w) => (w.tickers || []).includes(ticker)),
    [watchlists, ticker],
  )
  // `active` defaults to the pseudo-list 'all' (useWatchlists) — a write with
  // list_id 'all' 4xxs server-side while the toast already claimed success.
  // Resolve to a real list: the one holding the ticker (for remove), else the first.
  const realLists = (watchlists || []).filter((w) => w.id && w.id !== 'all')
  const containingList = ticker ? realLists.find((w) => (w.tickers || []).includes(ticker)) : null
  const targetListId = (active && active !== 'all') ? active : (containingList?.id ?? realLists[0]?.id)

  // entrance enhancement; base style is the resting visible state
  useEffect(() => { if (REDUCE) return; const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  // reset scroll on tab change
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0 }, [tab])

  function flash(m) { setToast(m); setTimeout(() => setToast(null), 1700) }
  function toggleWatch() {
    if (!ticker || !targetListId) { flash('Sign in to use watchlists'); return }
    if (watched) { removeTicker(targetListId, ticker); flash(`${ticker} removed`) }
    else { addTicker(targetListId, ticker); flash(`${ticker} added to watchlist`) }
  }

  // Pull-down-to-dismiss: drag the sheet straight down (down only); release past a
  // distance OR with a quick downward flick → close (slide out), else spring back.
  // No resize/peek — the grip does what the gesture looks like it does.
  const velRef = useRef({ y: 0, t: 0, v: 0 })
  function down(e) {
    const y = e.touches ? e.touches[0].clientY : e.clientY
    dragRef.current = { y }
    velRef.current = { y, t: Date.now(), v: 0 }
    setDragging(true)
  }
  useEffect(() => {
    if (!dragging) return
    function move(e) {
      if (!dragRef.current) return
      const y = e.touches ? e.touches[0].clientY : e.clientY
      const now = Date.now(), dt = now - velRef.current.t
      if (dt > 0) velRef.current.v = (y - velRef.current.y) / dt
      velRef.current.y = y; velRef.current.t = now
      setDragY(Math.max(0, y - dragRef.current.y)) // down only
    }
    function up() {
      dragRef.current = null; setDragging(false)
      const flick = velRef.current.v > 0.55 // px/ms — a quick downward flick
      if (dragY > 120 || flick) {
        const h = overlayRef.current ? overlayRef.current.clientHeight : 800
        setDragY(h); setTimeout(onClose, 220) // slide out, then unmount
      } else {
        setDragY(0) // didn't cross the line → spring back
      }
    }
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up)
    window.addEventListener('touchmove', move, { passive: true }); window.addEventListener('touchend', up)
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up) }
  }, [dragging, dragY, onClose])

  if (!stock) return null

  // ONE per-timeframe map for the whole sheet — the header below and the
  // OverviewTab PerfGrid both read it, so they cannot disagree. 1D–1Y are the
  // live snapshot's pre-computed buckets; 5Y/MAX have no snapshot bucket at
  // all and come from /api/overnight-split's own total for the same window
  // (close-to-close over the owned price_history). Those two are a day behind
  // the snapshot until the nightly append lands — immaterial on a multi-year
  // number, and the eyebrow labels them EOD rather than LIVE.
  const perf = {
    '1D': live.change1d, '5D': live.change1w ?? live.change5d, '1M': live.change1m, '6M': live.change6m, '1Y': live.change1y,
    '5Y': split.windows?.['5Y']?.total ?? null, MAX: split.windows?.MAX?.total ?? null,
  }
  // Falls back to the 1D direction only when the selected window has no number
  // at all (a ticker with no price_history) — in which case the % is hidden.
  const headChg = perf[tf] != null ? perf[tf] : (live.change1d || 0)
  const up = headChg >= 0
  const C = up ? '--studio-success' : '--studio-danger'
  const headColor = C
  const tier = stock.tier
  const alert = stock.cron_alert
  const alertTok = ALERT_TOKEN[alert] || null
  const liveMcapM = (stock.shares_outstanding && live.price) ? (stock.shares_outstanding * live.price) / 1_000_000 : null
  const mcap = fmtMcap(liveMcapM ?? stock.market_cap_usd_m)
  const oneLiner = stripNamePrefix(stock.one_liner, stock.name)

  return (
    <div ref={overlayRef} style={{ position: 'fixed', inset: 0, zIndex: 100 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', animation: (shown && !REDUCE) ? 'atlasFade 220ms ease' : 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: SNAP.full + '%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: `2px solid hsl(var(${C}) / 0.5)`, borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: `translateY(${dragY}px)`, transition: dragging ? 'none' : 'transform 240ms var(--studio-ease)',
        animation: (shown && !REDUCE) ? 'atlasSheet 340ms var(--studio-ease)' : 'none', overflow: 'hidden', maxWidth: 640, marginInline: 'auto', width: '100%' }}>

        {/* ── pinned header (drag area — pull down to dismiss) ── */}
        <div onMouseDown={down} onTouchStart={down}
          style={{ cursor: 'grab', padding: '9px 20px 0', flexShrink: 0, touchAction: 'none' }}>
          {/* grip */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 11 }}>
            <span style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))' }} />
          </div>
          {/* row 1: ticker + rating chip · buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
              <StockLogo ticker={stock.ticker} size={30} rounded={5} />
              <span style={{ ...Mono, fontSize: 22, color: 'hsl(var(--studio-text))', letterSpacing: '0.02em' }}>{stock.ticker}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <WatchBtn watch={watched} onClick={toggleWatch} />
              <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, flexShrink: 0, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, color: 'hsl(var(--studio-text-muted))', fontSize: 15 }}>✕</button>
            </div>
          </div>
          {/* row 2: company name + one-liner · price + % */}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4, gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'hsl(var(--studio-text-subtle))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stock.name}</div>
              {oneLiner && <div style={{ fontFamily: 'var(--studio-font-display)', fontStyle: 'italic', fontSize: 12, color: 'hsl(var(--studio-text-subtle))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{oneLiner}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexShrink: 0 }}>
              {live.price != null && <span style={{ ...Mono, fontSize: 17, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>{live.price.toFixed(2)}</span>}
              {perf[tf] != null && (
                <span style={{ ...Mono, fontSize: 11.5, color: `hsl(var(${headColor}))`, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {up ? '▲' : '▼'} {fmtAbsPct(headChg)}% <span style={{ color: 'hsl(var(--studio-text-subtle))', fontSize: 10 }}>{tf}</span>
                </span>
              )}
            </div>
          </div>
          {/* badge row */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
            {tier && tier !== 'main' && (
              <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)', background: 'hsl(var(--studio-accent) / 0.14)', border: '1px solid hsl(var(--studio-accent) / 0.4)', color: 'hsl(var(--studio-accent))' }}>{tier.toUpperCase()} TIER</span>
            )}
            {alertTok && alert !== 'green' && alert !== 'none' && (
              <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)', background: `hsl(var(${alertTok}) / 0.14)`, border: `1px solid hsl(var(${alertTok}) / 0.4)`, color: `hsl(var(${alertTok}))` }}>{alert.toUpperCase()} ALERT</span>
            )}
            {mcap && (
              <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 'var(--studio-radius-sm)', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))' }}>{mcap} MCAP</span>
            )}
          </div>
          {/* tab bar */}
          <div style={{ display: 'flex', gap: 22, borderBottom: '1px solid hsl(var(--studio-border) / 0.6)', marginTop: 14 }}>
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px',
                ...Mono, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
                color: tab === t.id ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))',
                borderBottom: tab === t.id ? '2px solid hsl(var(--studio-accent))' : '2px solid transparent', marginBottom: -1, transition: 'color 160ms' }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* ── scroll body ── */}
        <div ref={bodyRef} className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '18px 20px 30px' }}>
          {tab === 'overview' && <OverviewTab stock={stock} live={live} fund={fund} perf={perf} split={split} tf={tf} setTf={setTf} type={type} setType={setType}
            indicators={indicators} setIndicators={setIndicators} gearOpen={gearOpen} setGearOpen={setGearOpen} advOpen={advOpen} setAdvOpen={setAdvOpen}
            scaleMode={scaleMode} setScaleMode={setScaleMode} interval={interval} setInterval={setInterval}
            measure={measure} setMeasure={setMeasure} />}
          {tab === 'earnings' && <EarningsTab stock={stock} />}
          {tab === 'outlook' && <OutlookTab stock={stock} />}
        </div>

        {toast && (
          <div style={{ position: 'absolute', left: '50%', bottom: 22, transform: 'translateX(-50%)', zIndex: 5, ...Mono, fontSize: 11, letterSpacing: '0.04em',
            padding: '9px 16px', borderRadius: 999, background: 'hsl(var(--studio-surface))', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent))', boxShadow: '0 6px 20px hsl(var(--studio-overlay) / 0.5)', animation: REDUCE ? 'none' : 'atlasFade 200ms ease' }}>{toast}</div>
        )}
      </div>
    </div>
  )
}

